import type { Undefinable } from "@core/domain/types/undefinable.type";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import type { EntityApiKey } from "@entity-api-keys/domain/models/entity-api-key.model";
import { UpdateEntityApiKey } from "@entity-api-keys/domain/models/update-entity-api-key.model";
import { GetEntityApiKeyByIdUseCase } from "@entity-api-keys/domain/usecases/get-entity-api-key-by-id.usecase";
import { UpdateEntityApiKeyUseCase } from "@entity-api-keys/domain/usecases/update-entity-api-key.usecase";
import { inject, injectable } from "inversify";
import {
    action,
    flow,
    flowResult,
    makeObservable,
    observable,
    runInAction,
} from "mobx";

export interface EditEntityApiKeyFormValuesValidated {
    entityId: number | null;
    name: string;
    scopes: string[];
    isActive: boolean;
}

@injectable()
export class EditEntityApiKeyPageViewModel extends BaseViewModel {
    apiKeyId: Undefinable<number> = undefined;

    @observable
    initiallyLoading: boolean = true;

    @observable
    apiKey: Undefinable<EntityApiKey> = undefined;

    constructor(
        @inject(GetEntityApiKeyByIdUseCase)
        private readonly getByIdUseCase: GetEntityApiKeyByIdUseCase,
        @inject(UpdateEntityApiKeyUseCase)
        private readonly updateUseCase: UpdateEntityApiKeyUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        this.initViewData();
    }

    @flow
    async *initViewData(): AsyncGenerator<void> {
        yield flowResult(this.load());
        runInAction(() => {
            this.initiallyLoading = false;
        });
    }

    async load(): Promise<void> {
        if (this.apiKeyId === undefined) throw new Error("Missing apiKeyId");

        const key = await this.getByIdUseCase.execute(this.apiKeyId);
        if (key) {
            this.setApiKey(key);
        }
    }

    @action
    setApiKey(key: EntityApiKey): void {
        this.apiKey = key;
    }

    async save(
        values: EditEntityApiKeyFormValuesValidated,
        options: { canToggleActivation: boolean },
    ): Promise<boolean> {
        if (this.apiKeyId === undefined) throw new Error("Missing apiKeyId");

        LoadLayoutStore.start();

        const nextEntityId =
            values.entityId !== null &&
            values.entityId !== this.apiKey?.entityId
                ? values.entityId
                : undefined;

        const nextIsActive =
            options.canToggleActivation &&
            values.isActive !== this.apiKey?.isActive
                ? values.isActive
                : undefined;

        const model = new UpdateEntityApiKey(
            this.apiKeyId,
            nextEntityId,
            values.name,
            values.scopes,
            nextIsActive,
        );

        const updated = await this.updateUseCase.execute(model);

        LoadLayoutStore.finish();

        if (updated) {
            ToastManagerStore.success();
            this.setApiKey(updated);
            return true;
        }

        return false;
    }
}
